/**
 * The happiness HUD: the meter, the place list, and the bliss toast.
 *
 * Same contract as hud.js's ChargeMeter, scaled up: everything on screen is
 * one static DOM built once in the constructor, and every update writes only
 * classes, custom properties and textContent -- never structure.  The meter's
 * setHappiness runs every frame from the render loop, so it is quantised and
 * cached exactly like --charge; setNeeds and setRoster arrive on server
 * messages (a handful a second at most) but keep the same discipline anyway,
 * because "cheap enough for a frame" is the only budget that never needs
 * re-auditing when a call site moves.
 *
 * The rows are built from shared/happy.js's PLACES rather than written into
 * index.html: thirteen labels in markup would be a second copy of the table,
 * and the first place renamed on the server would leave the page lying about
 * it.  Same bargain the customizer panel strikes with #customizer.
 */
import {
  PLACES, PLACE_BY_ID, PLACE_INDEX, HOW, POUNCE_HITS, WALK_METRES,
  HUNT_CAPTURE, FINDS, FIND_BY_ID,
} from "@catworld/shared";
import { PURR_AT, DWELL, FIND_STATE } from "@catworld/shared";

/** How long the bliss toast holds.  Long enough to read a name and a place
    at a glance, short enough that two blisses in a row still both show. */
const BLISS_MS = 3200;

/** createElement + textContent only: place labels are trusted, but cat names
    in the toast are user text and must never travel through innerHTML. */
function el(tag, className, text) {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (text !== undefined) node.textContent = text;
  return node;
}

export class HappyHud {
  constructor(root, bliss) {
    this.el = root;
    this.meter = root.querySelector("#happy-meter");
    this.word = root.querySelector("#happy-word");
    this.roster = root.querySelector("#happy-roster");
    this.tally = root.querySelector("#happy-tally");
    this.finds = root.querySelector("#happy-finds");
    this.next = root.querySelector("#happy-next");
    this.list = root.querySelector("#happy-list");
    // The toast is a sibling, not a child: it is fixed top-centre because the
    // moment belongs to the whole street, not to this panel's corner.  Handed
    // in beside the root (ui/happy.js makes both) -- this module queries only
    // inside what it was given.
    this.bliss_ = bliss;
    this.blissTimer = 0;

    // The notch's position comes from the constant, written once.  CSS with a
    // literal 72% in it would be a copy of PURR_AT waiting to drift.
    this.meter?.style.setProperty("--purr-at", PURR_AT);

    // main.js zooms the camera on window wheel; scrolling this list must not.
    root.addEventListener("wheel", (e) => e.stopPropagation());

    // ---- caches, so every setter can no-op ----
    this.shown = -1;                    // last --happy written
    this.purring = false;
    this.rosterShown = "";
    this.liveIdx = -1;                  // row currently being visited
    this.progShown = "";                // last progress text
    this.tShown = -1;                   // last --t written
    this.nextShown = -1;                // last "next thing to do" index
    this.tallyShown = "";               // last collection line
    this.findsShown = "";               // last treasure line
    this.carryShown = null;             // species in our cat's mouth, or null

    this.rows = this.buildRows();
    this.nextParts = this.buildNext();
  }

  /**
   * The thirteen rows, in PLACES order -- which shared/happy.js keeps as
   * "solo first", so the two section headers fall out of one walk over the
   * table.
   */
  buildRows() {
    const rows = [];
    let sectioned = false;
    this.list.appendChild(el("div", "hp-h", "Manages alone"));
    for (const place of PLACES) {
      if (!place.solo && !sectioned) {
        sectioned = true;
        this.list.appendChild(el("div", "hp-h you", "Needs you"));
      }
      const row = el("div", place.solo ? "hp-row" : "hp-row you");
      row.title = place.hint;           // tooltip on every row, cheap and out of the way
      row.append(
        el("span", "hp-chip", place.solo ? "cat" : "you"),
        el("span", "hp-name", place.label),
        el("i", "hp-want"),
      );
      // The in-progress line: hidden except on the .live row.  Text plus a
      // bar, because "3 pounces" and "3.2 m" are rules the bar alone cannot
      // teach a player who has never seen them.
      const progText = el("span");
      const prog = el("span", "hp-prog");
      prog.append(progText, el("i"));
      row.appendChild(prog);
      // The expanded hint only for the rows that need hands: the solo six
      // will be visited whether or not anyone reads their hint, and a
      // panel where every row expanded stopped being glanceable long ago.
      if (!place.solo) row.appendChild(el("span", "hp-hint", place.hint));
      this.list.appendChild(row);
      rows.push({ row, prog, progText, wantShown: -1, done: false });
    }
    return rows;
  }

  /** The phone line: one chip + name + hint, filled by whichever place is
      currently the most wanted.  Built always, shown only under 640px. */
  buildNext() {
    const chip = el("span", "hp-chip");
    const name = el("span", "hp-name");
    const hint = el("span", "hp-hint");
    this.next.append(el("div", "hp-h", "Next"), chip, name, hint);
    return { chip, name, hint };
  }

  /** happiness 0..1, every frame.  Quantised to 1/100 -- under 2.2px on the
      220px track -- so a held value writes nothing at all. */
  setHappiness(v) {
    const step = Math.max(0, Math.min(100, Math.round(v * 100))) / 100;
    if (step === this.shown) return;
    this.shown = step;
    this.el.style.setProperty("--happy", step);
    // Compared on the quantised value so the accent flip and the notch agree
    // to the pixel about where the purr starts.
    const purring = step >= PURR_AT;
    if (purring !== this.purring) {
      this.purring = purring;
      this.el.classList.toggle("purring", purring);
    }
  }

  /**
   * wants: number[] in PLACES order, 1 = fully wanted, 0 = just satisfied.
   * progress: { place, t } for the place mid-visit, or null.
   */
  setNeeds(wants, progress) {
    if (!Array.isArray(wants)) return;
    let hungriest = -1;
    let hunger = -1;
    for (let i = 0; i < this.rows.length; i++) {
      const r = this.rows[i];
      const raw = typeof wants[i] === "number" ? wants[i] : 0;
      // 1/20 steps: 1.7px on the 34px want bar, and a want regrowing at
      // 1/540 per second then writes once every 27s instead of per message.
      const w = Math.max(0, Math.min(20, Math.round(raw * 20))) / 20;
      if (w !== r.wantShown) {
        r.wantShown = w;
        r.row.style.setProperty("--want", w);
        const done = w <= 0.05;
        if (done !== r.done) {
          r.done = done;
          r.row.classList.toggle("done", done);
        }
      }
      if (w > hunger) { hunger = w; hungriest = i; }
    }
    this.setProgress(progress);
    this.setNextRow(hungriest, hunger);
  }

  /** Mark the row mid-visit and word its countdown in the visit's own rule. */
  setProgress(progress) {
    const idx = progress && progress.place != null
      ? (PLACE_INDEX[progress.place] ?? -1) : -1;
    if (idx !== this.liveIdx) {
      if (this.liveIdx >= 0) this.rows[this.liveIdx].row.classList.remove("live");
      if (idx >= 0) this.rows[idx].row.classList.add("live");
      this.liveIdx = idx;
      this.progShown = "";
      this.tShown = -1;
    }
    if (idx < 0) return;
    const r = this.rows[idx];
    const place = PLACES[idx];
    const t = Math.max(0, Math.min(1, typeof progress.t === "number" ? progress.t : 0));
    // Each rule counts down in its own unit, because the generic bar cannot
    // teach a rule the player has never seen: POUNCE reads as pounces left,
    // WALK as metres left, DWELL as seconds of staying put.  The 1e-9 nudges
    // keep an exact server t (1/3, 2/5...) from ceiling into the next step.
    let text;
    if (place.how === HOW.POUNCE) {
      const left = Math.max(1, POUNCE_HITS - Math.floor(t * POUNCE_HITS + 1e-9));
      text = left === 1 ? "last pounce!" : `${left} pounces to go`;
    } else if (place.how === HOW.HUNT) {
      // Defensive only: the server never reports the hunt as visit progress
      // (a hunt is not a zone visit -- see happy.js), so the live carry line
      // arrives through setCarry instead.  If a future server ever does
      // report it, at least the words are right.
      text = "carry it home";
    } else if (place.how === HOW.SEEK) {
      // Same defensive-only footing as HUNT: placeAt skips SEEK rows, so
      // this line is never reported as progress today.
      text = "dig it out";
    } else if (place.how === HOW.WALK) {
      const m = Math.max(0.1, Math.ceil((1 - t) * WALK_METRES * 10) / 10);
      text = `${m.toFixed(1)} m to go`;
    } else {
      const s = Math.max(1, Math.ceil((1 - t) * DWELL - 1e-9));
      text = `stay put · ${s}s`;
    }
    if (text !== this.progShown) {
      this.progShown = text;
      r.progText.textContent = text;
    }
    const step = Math.round(t * 50) / 50;
    if (step !== this.tShown) {
      this.tShown = step;
      r.prog.style.setProperty("--t", step);
    }
  }

  /**
   * Our own cat has a catch in its mouth (species string), or not (null).
   * Driven off the prey rows by main.js rather than off visit progress,
   * because a carry is not a visit: it survives a nap on the deck halfway
   * home, and the server's progress slot rightly reports the nap.
   *
   * The row borrows the .live treatment and words the deal plainly --
   * "worth double" is the entire reason to make the trip, so it is said at
   * the exact moment the player is deciding whether to make it.  The bar
   * shows HUNT_CAPTURE: the capture's third is banked, the doubled rest
   * rides home in the mouth.  No index fight with setProgress is possible:
   * progress.place is never "hunt" (happiness.js's placeAt skips HUNT
   * rows), so liveIdx never points here.
   */
  setCarry(species) {
    if (species === this.carryShown) return;
    this.carryShown = species;
    const r = this.rows[PLACE_INDEX.hunt];
    if (!r) return;
    if (species) {
      r.row.classList.add("live");
      r.progText.textContent = `carry the ${species} home · worth double`;
      r.prog.style.setProperty("--t", HUNT_CAPTURE);
    } else {
      r.row.classList.remove("live");
    }
  }

  /**
   * The collection readout: what this cat has hunted, forever.
   * tal = { mouse, bird, gifts } off the needs message; absent or all-zero
   * renders nothing (the :empty CSS hides the line), so a cat that has
   * never hunted is not haunted by a row of zeros.
   */
  setTally(tal) {
    if (!tal || typeof tal !== "object") return;
    const m = tal.mouse | 0, b = tal.bird | 0, g = tal.gifts | 0;
    const parts = [];
    if (m) parts.push(`${m} ${m === 1 ? "mouse" : "mice"}`);
    if (b) parts.push(`${b} ${b === 1 ? "bird" : "birds"}`);
    const text = parts.length
      ? `caught ${parts.join(" · ")} — ${g} brought home`
      : "";
    if (text === this.tallyShown) return;
    this.tallyShown = text;
    if (this.tally) this.tally.textContent = text;
  }

  /**
   * The treasure line: how much of the street's secret this cat has dug up,
   * off the owner-only `finds` message (one FIND_STATE per FINDS row).
   *
   * The count leads and the SHORT names follow, because the names are half
   * the reward (a row of "3/7" is a progress bar; "hair tie · shiny coin ·
   * jingly ball" is a collection).  Unfound treasures appear nowhere --
   * not even as "?" slots -- so the line never becomes a checklist of
   * exactly how many corners are left to sweep; the seek row's hint is the
   * only up-front promise.  The title tooltip carries each found
   * treasure's full label and its where-it-was hint: the little story is
   * the discovery's earned flavour, and a tooltip keeps seven stories from
   * costing seven lines of panel.
   */
  setFinds(f) {
    if (!Array.isArray(f) || !this.finds) return;
    const got = FINDS.filter((find, i) =>
      f[i] === FIND_STATE.TAKEN || f[i] === FIND_STATE.BACK);
    const text = got.length
      ? `${got.length} of ${FINDS.length} treasures — ${
          got.map((find) => find.short).join(" · ")}`
      : "";
    if (text === this.findsShown) return;
    this.findsShown = text;
    this.finds.textContent = text;
    this.finds.title = got.map((find) => `${find.label} — ${find.hint}`).join("\n");
  }

  /**
   * A treasure dug out, through the bliss toast (last-wins as ever).  A
   * DISCOVERY (first) arrives broadcast and is worded as the event it is;
   * a re-find arrives owner-only and is worded as the small pleasure it
   * is.  `mine` earns the first-person flourish on a discovery -- the
   * where-it-was hint -- because the finder already knows where they are
   * standing, so for them the hint reads as the story, not a spoiler.
   */
  found(id, name, mine, first) {
    if (!this.bliss_) return;
    const find = FIND_BY_ID[id];
    if (!find) return;               // a treasure this build has never heard of
    this.bliss_.querySelector("b").textContent = name || "A cat";
    this.bliss_.querySelector("span").textContent =
      !first ? `✓ found ${find.label} again`
      : mine ? `✦ dug up ${find.label} — ${find.hint}`
      : `✦ dug up ${find.label}`;
    this.bliss_.classList.add("show");
    clearTimeout(this.blissTimer);
    this.blissTimer = setTimeout(() => this.bliss_.classList.remove("show"), BLISS_MS);
  }

  /**
   * A hunt moment, street-wide, through the same toast as a bliss (last-wins
   * for the same reason).  `mine` flavours our own capture with the next
   * move -- the one time "worth double" is a prompt rather than a receipt.
   */
  hunted(kind, species, name, mine) {
    if (!this.bliss_) return;
    const s = species === "bird" ? "bird" : "mouse";
    this.bliss_.querySelector("b").textContent = name || "A cat";
    this.bliss_.querySelector("span").textContent =
      kind === "home" ? `✓ brought a ${s} home — worth double`
      : mine ? `caught a ${s}! carry it into the house for double`
      : `caught a ${s}`;
    this.bliss_.classList.add("show");
    clearTimeout(this.blissTimer);
    this.blissTimer = setTimeout(() => this.bliss_.classList.remove("show"), BLISS_MS);
  }

  /** The phone line follows the hungriest want; edge-triggered on the index
      so a stable leader writes nothing. */
  setNextRow(idx, hunger) {
    // Everything sated (a freshly perfect cat): keep showing the last leader
    // rather than flickering the line empty for a few seconds.
    if (idx < 0 || hunger <= 0.05 || idx === this.nextShown) return;
    this.nextShown = idx;
    const place = PLACES[idx];
    this.nextParts.chip.textContent = place.solo ? "cat" : "you";
    this.next.classList.toggle("you", !place.solo);
    this.nextParts.name.textContent = place.label;
    this.nextParts.hint.textContent = place.hint;
  }

  /** Optional roster line: how alive the street is. */
  setRoster(n, humans) {
    const text = typeof n !== "number" || n <= 0 ? ""
      : `${n} cat${n === 1 ? "" : "s"} on the street`
        + (humans > 0 ? ` · ${humans} being played` : "");
    if (text === this.rosterShown) return;
    this.rosterShown = text;
    this.roster.textContent = text;
  }

  /** Somebody banked a place.  Last-wins on purpose: with sixteen cats two
      blisses can land in one second, and a queue would still be replaying
      the first while the street has moved on. */
  bliss(placeId, name) {
    if (!this.bliss_) return;
    const label = PLACE_BY_ID[placeId]?.label ?? placeId;
    this.bliss_.querySelector("b").textContent = name || "A cat";
    // The labels are imperatives ("Play with the butterflies"); lower-cased
    // after a tick they read as the thing that just got done.
    this.bliss_.querySelector("span").textContent =
      "✓ " + label.charAt(0).toLowerCase() + label.slice(1);
    this.bliss_.classList.add("show");
    clearTimeout(this.blissTimer);
    this.blissTimer = setTimeout(() => this.bliss_.classList.remove("show"), BLISS_MS);
  }

  /** For main.js: the panel means nothing until a cat has wants to show. */
  show() { this.el.classList.remove("hidden"); }
  hide() { this.el.classList.add("hidden"); }
}
